import React, { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Button } from './ui/button';
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Self-hosted PDF.js worker, served same-origin from /public with a plain `.js`
// extension. pdf.js v5 spawns a MODULE worker (new Worker(src, {type:'module'}))
// which the browser only accepts when served with a valid JavaScript MIME type.
// IIS serves `.js` as application/javascript by default (no MIME config needed),
// whereas `.mjs` 404s unless a staticContent mapping is added — so `.js` is the
// robust, deploy-proof choice that satisfies the strict CSP worker-src 'self'.
pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

const PDFViewer = ({ url, onError, zoom, onPageChange, hideToolbar = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [internalScale, setInternalScale] = useState(1.0);
  const [loading, setLoading] = useState(true);

  // If parent passes `zoom`, it's controlled. Otherwise PDFViewer manages its own scale.
  const scale = typeof zoom === 'number' ? zoom : internalScale;
  const setScale = typeof zoom === 'number' ? () => {} : setInternalScale;

  const onDocumentLoadSuccess = useCallback(({ numPages: total }) => {
    setNumPages(total);
    setLoading(false);
    if (onPageChange) onPageChange(pageNumber, total);
  }, [onPageChange, pageNumber]);

  const onDocumentLoadError = useCallback((error) => {
    console.error('PDF load error:', error);
    setLoading(false);
    if (onError) onError(error);
  }, [onError]);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => {
      const next = Math.max(prev - 1, 1);
      if (onPageChange) onPageChange(next, numPages);
      return next;
    });
  }, [numPages, onPageChange]);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => {
      const next = Math.min(prev + 1, numPages || 1);
      if (onPageChange) onPageChange(next, numPages);
      return next;
    });
  }, [numPages, onPageChange]);

  // Expose page nav handlers via a global ref so the fullscreen toolbar can drive them.
  // (Simple imperative bridge for keyboard shortcuts in the parent overlay.)
  React.useEffect(() => {
    if (onPageChange) onPageChange(pageNumber, numPages);
  }, [pageNumber, numPages, onPageChange]);

  // Listen for external nav events (used by fullscreen overlay keyboard shortcuts)
  React.useEffect(() => {
    const onExternalNav = (e) => {
      const dir = e?.detail?.direction;
      if (dir === 'prev') goToPrevPage();
      if (dir === 'next') goToNextPage();
    };
    window.addEventListener('pdfviewer:nav', onExternalNav);
    return () => window.removeEventListener('pdfviewer:nav', onExternalNav);
  }, [goToPrevPage, goToNextPage]);

  const zoomIn = () => setScale((prev) => Math.min(prev + 0.25, 3.0));
  const zoomOut = () => setScale((prev) => Math.max(prev - 0.25, 0.5));
  const resetZoom = () => setScale(1.0);

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar (hidden when used inside a parent fullscreen overlay) */}
      {!hideToolbar && (
      <div className="flex items-center justify-between p-2 bg-slate-100 border-b rounded-t-lg">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            data-testid="pdf-prev-page"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600 min-w-[100px] text-center">
            Page {pageNumber} of {numPages || '-'}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={goToNextPage}
            disabled={pageNumber >= (numPages || 1)}
            data-testid="pdf-next-page"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomOut}
            disabled={scale <= 0.5}
            data-testid="pdf-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-sm text-slate-600 min-w-[60px] text-center">
            {Math.round(scale * 100)}%
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={zoomIn}
            disabled={scale >= 3.0}
            data-testid="pdf-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={resetZoom}
            data-testid="pdf-reset-zoom"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      )}

      {/* PDF Content */}
      <div className="flex-1 overflow-auto bg-slate-200 flex justify-center p-4" style={{ transformOrigin: 'top center' }}>
        {loading && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        )}
        <Document
          file={url}
          onLoadSuccess={onDocumentLoadSuccess}
          onLoadError={onDocumentLoadError}
          loading={null}
          className="shadow-lg"
        >
          <Page
            pageNumber={pageNumber}
            scale={scale}
            renderTextLayer={true}
            renderAnnotationLayer={true}
            className="bg-white"
          />
        </Document>
      </div>
    </div>
  );
};

export default PDFViewer;
